import React, { useCallback, useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/autoplay";
import { Navigation, Autoplay } from "swiper/modules";
import Image from "next/image";
import styles from "./HomeTestimonials.module.css";
import Link from "next/link";
import { ChevronRight, ChevronLeft } from "lucide-react";

interface TestimonialImage {
  id: number;
  documentId: string;
  url: string;
  alternativeText: string;
}

interface TestimonialLink {
  id: number;
  label: string;
  url: string;
}

interface Testimonial {
  id: number;
  title: string;
  description: string;
  sub_title: string;
  image: TestimonialImage[];
  link_text_image: {
    id: number;
    link: TestimonialLink;
    image: TestimonialImage;
  };
}

interface HomeTestimonialsProps {
  data: {
    id: number;
    cards: Testimonial[];
  };
}

const HomeTestimonials: React.FC<HomeTestimonialsProps> = ({ data }) => {
  const [isMobileView, setIsMobileView] = useState<boolean>(false);

  const handleResize = useCallback(() => {
    setIsMobileView(window.innerWidth <= 991);
  }, []);

  useEffect(() => {
    handleResize();
    if (typeof window !== "undefined") {
      window.addEventListener("resize", handleResize);

      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }
  }, [handleResize]);

  return (
    <div className={styles.testimonialWrapper}>
      <div className="container">
        <Swiper
          navigation={{
            nextEl: `.${styles.swiperButtonNext}`,
            prevEl: `.${styles.swiperButtonPrev}`,
          }}
          modules={[Navigation, Autoplay]}
          spaceBetween={50}
          slidesPerView={1}
          loop={true}
          autoplay={{
            delay: 7000,
            disableOnInteraction: false,
          }}
          className={styles.swiperContainer}
        >
          {data?.cards?.map((testimonial: Testimonial) => (
            <SwiperSlide key={testimonial?.id}>
              <div className={styles.testimonialSlide}>
                <div className={styles.imageWrapper}>
                  <Image
                    src={testimonial?.image[0]?.url}
                    alt={
                      testimonial?.image[0]?.alternativeText || "testimonial"
                    }
                    title={
                      testimonial?.image[0]?.alternativeText || "testimonial"
                    }
                    width={500}
                    height={500}
                    loading="lazy"
                  />
                </div>
                <h3>{testimonial?.title}</h3>
                <span>{testimonial?.sub_title}</span>
                <p>{testimonial?.description}</p>
                {testimonial?.link_text_image?.link?.url && (
                  <Link
                    href={testimonial.link_text_image.link.url}
                    shallow
                    passHref
                    title={testimonial.link_text_image.link.url}
                  >
                    {isMobileView
                      ? "Read more"
                      : testimonial.link_text_image.link.label}
                    <span className={styles.arrow}>
                      <ChevronRight />
                    </span>
                  </Link>
                )}
              </div>
            </SwiperSlide>
          ))}

          <div className={`${styles.swiperButtonPrev}`} aria-hidden="true">
            <ChevronLeft />
          </div>
          <div className={`${styles.swiperButtonNext}`} aria-hidden="true">
            <ChevronRight />
          </div>
        </Swiper>
      </div>
    </div>
  );
};

export default HomeTestimonials;
